import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import BarraFiltri from "@/components/BarraFiltri";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { fileUrl } from "@/lib/placeholders";
import { useDaCerca } from "@/lib/cerca";
import { puoGestire } from "@/lib/ruoli";
import BottoneRaccogli from "@/components/BottoneRaccogli";
import { Plus, FileText, ExternalLink, Archive, Trash2, Edit, RotateCcw, ArrowLeft } from "lucide-react";

export default function DocumentiAziendali() {
  const { user } = useAuth();
  const canManage = user?.role === "admin" || user?.role === "amministrativo" || user?.role === "tecnico";
  // Eliminare un documento aziendale e' `require_gestione`.
  const isAdmin = puoGestire(user);
  const [list, setList] = useState([]);
  const [cats, setCats] = useState([]);
  const [catFilter, setCatFilter] = useState("all");
  // Sono decine, e crescono: le pastiglie da sole non bastano piu'.
  const [cerca, setCerca] = useState("");
  useDaCerca("cerca", setCerca);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [editing, setEditing] = useState(null);
  const [archivio, setArchivio] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      if (archivio) {
        const { data } = await api.get("/company-documents/archived");
        setList(data);
      } else {
        const params = catFilter && catFilter !== "all" ? `?category=${encodeURIComponent(catFilter)}` : "";
        const { data } = await api.get(`/company-documents${params}`);
        setList(data);
      }
    } catch { toast.error("Errore caricamento documenti"); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    api.get("/company-documents/categories").then(({ data }) => setCats(data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [catFilter, archivio]);

  const archive = async (d) => {
    if (!window.confirm(`Archiviare "${d.title}"?\n\nResta consultabile dall'archivio e puoi ripristinarlo quando vuoi.`)) return;
    try {
      await api.post(`/company-documents/${d.id}/archive`);
      toast.success("Documento archiviato");
      load();
    } catch { toast.error("Errore"); }
  };

  const restore = async (d) => {
    try {
      await api.post(`/company-documents/${d.id}/restore`);
      toast.success("Documento ripristinato");
      load();
    } catch { toast.error("Errore"); }
  };

  const del = async (d) => {
    if (!window.confirm(
      `Eliminare definitivamente "${d.title}"?\n\n` +
      `Vengono cancellati sia la scheda sia il file allegato.\n` +
      `L'operazione non è reversibile.`
    )) return;
    try {
      await api.delete(`/company-documents/${d.id}`);
      toast.success("Documento eliminato");
      load();
    } catch { toast.error("Errore"); }
  };

  const today = new Date().toISOString().substring(0, 10);

  const q = cerca.trim().toLowerCase();
  const visibili = q
    ? list.filter((d) => `${d.title} ${d.category} ${d.notes || ""}`
        .toLowerCase().includes(q))
    : list;

  return (
    <div className="space-y-6" data-testid="documenti-aziendali-page">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-orange-600 mb-2">Documenti</div>
          <h1 className="font-display text-4xl font-black tracking-tighter">
            {archivio ? "Documenti archiviati" : "Documenti aziendali"}
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            {archivio ? "Documenti non più in uso · ripristinabili o eliminabili"
                      : "CCIAA, DURC, incarichi, nomine, certificazioni"}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" className="h-12" onClick={() => setArchivio(!archivio)}
                  data-testid="archivio-docs-btn">
            {archivio ? <><ArrowLeft className="w-4 h-4 mr-2" /> Torna ai documenti</>
                      : <><Archive className="w-4 h-4 mr-2" /> Archiviati</>}
          </Button>
          {canManage && !archivio && (
            <Button className="bg-orange-500 hover:bg-orange-600 text-white h-12" onClick={() => setShowNew(true)}>
              <Plus className="w-4 h-4 mr-2" /> Carica documento
            </Button>
          )}
        </div>
      </div>

      <BarraFiltri
        filtri={archivio ? [] : [{
          valore: catFilter === "all" ? "" : catFilter,
          onChange: (v) => setCatFilter(v || "all"),
          tutti: "Tutti i tipi",
          opzioni: cats,
          testid: "filtro-tipo",
        }]}
        cerca={cerca} onCerca={setCerca}
        segnaposto="Titolo o tipo di documento..."
        conteggio={visibili.length} etichetta="documenti" />

      {loading && <p className="text-sm text-slate-500 py-8 text-center">Caricamento...</p>}
      {!loading && visibili.length === 0 && (
        <Card className="border-dashed border-2"><CardContent className="p-10 text-center text-slate-500">
          {archivio ? "Nessun documento archiviato." : "Nessun documento in questa categoria."}
        </CardContent></Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2.5">
        {visibili.map(d => {
          const expired = d.expires_date && d.expires_date < today;
          const expiring = d.expires_date && !expired && d.expires_date <= new Date(Date.now() + 60 * 86400000).toISOString().substring(0, 10);
          return (
            <Card key={d.id} className={`border-slate-200 ${archivio ? "bg-slate-50" : expired ? "border-red-200 bg-red-50" : expiring ? "border-amber-200 bg-amber-50" : ""}`}>
              <CardContent className="p-3">
                <div className="flex items-start gap-2">
                  <FileText className={`w-5 h-5 flex-shrink-0 mt-0.5 ${archivio ? "text-slate-400" : "text-orange-500"}`} />
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm truncate">{d.title}</div>
                    <Badge variant="outline" className="text-[10px] mt-1">{d.category}</Badge>
                  </div>
                </div>
                <div className="text-xs text-slate-500 mt-1">
                  {d.issued_date && <>Emesso {d.issued_date.substring(0, 10)}</>}
                  {d.expires_date && <> · Scade {d.expires_date.substring(0, 10)}</>}
                  {!archivio && expired && <span className="text-red-600 font-semibold"> · SCADUTO</span>}
                  {!archivio && expiring && <span className="text-amber-700 font-semibold"> · in scadenza</span>}
                </div>
                {d.notes && <p className="text-xs text-slate-400 mt-1">{d.notes}</p>}
                <div className="flex gap-1.5 mt-2 flex-wrap">
                  <a href={fileUrl(d.file_url)} target="_blank" rel="noopener noreferrer">
                    <Button variant="outline" size="sm"><ExternalLink className="w-3 h-3 mr-1" />Apri</Button>
                  </a>
                  {d.file_url && (
                    <BottoneRaccogli className="h-9 w-9 p-0"
                                     voce={{ tipo: "azienda", id: d.id, gruppo: "Documenti aziendali",
                                             titolo: d.title, sottotitolo: d.category }} />
                  )}
                  {canManage && !archivio && (
                    <>
                      <Button variant="outline" size="sm" onClick={() => setEditing(d)} title="Modifica dati">
                        <Edit className="w-3 h-3" />
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => archive(d)} title="Archivia">
                        <Archive className="w-3 h-3" />
                      </Button>
                    </>
                  )}
                  {canManage && archivio && (
                    <Button variant="outline" size="sm" onClick={() => restore(d)} title="Rimetti tra i documenti attivi">
                      <RotateCcw className="w-3 h-3 mr-1" /> Ripristina
                    </Button>
                  )}
                  {isAdmin && (
                    <Button variant="outline" size="sm" onClick={() => del(d)} className="text-red-600"
                            title="Elimina definitivamente">
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {showNew && <DocDialog cats={cats} onClose={() => setShowNew(false)}
                             onSaved={() => { setShowNew(false); load(); }} />}
      {editing && <DocDialog cats={cats} initial={editing} onClose={() => setEditing(null)}
                             onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function DocDialog({ cats, initial, onClose, onSaved }) {
  const modifica = Boolean(initial);
  const [category, setCategory] = useState(initial?.category || cats[0] || "Altro");
  const [title, setTitle] = useState(initial?.title || "");
  const [issuedDate, setIssuedDate] = useState((initial?.issued_date || "").substring(0, 10));
  const [expiresDate, setExpiresDate] = useState((initial?.expires_date || "").substring(0, 10));
  const [notes, setNotes] = useState(initial?.notes || "");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!title.trim()) { toast.error("Il titolo è obbligatorio"); return; }
    if (!modifica && !file) { toast.error("Il file è obbligatorio"); return; }
    setBusy(true);
    try {
      if (modifica) {
        await api.put(`/company-documents/${initial.id}`, {
          category, title,
          issued_date: issuedDate || null,
          expires_date: expiresDate || null,
          notes: notes || null,
        });
        toast.success("Documento aggiornato");
      } else {
        const fd = new FormData();
        fd.append("category", category); fd.append("title", title);
        if (issuedDate) fd.append("issued_date", issuedDate);
        if (expiresDate) fd.append("expires_date", expiresDate);
        if (notes) fd.append("notes", notes);
        fd.append("file", file);
        await api.post("/company-documents", fd, { headers: { "Content-Type": "multipart/form-data" } });
        toast.success("Documento caricato");
      }
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{modifica ? "Modifica documento" : "Carica documento aziendale"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div><Label>Categoria</Label>
            <select className="w-full h-10 border border-slate-200 rounded px-3" value={category}
                    onChange={(e) => setCategory(e.target.value)}>
              {cats.map(c => <option key={c}>{c}</option>)}
            </select>
          </div>
          <div><Label>Titolo *</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="es. Visura CCIAA 2026" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Data emissione</Label>
              <Input type="date" value={issuedDate} onChange={(e) => setIssuedDate(e.target.value)} /></div>
            <div><Label>Data scadenza</Label>
              <Input type="date" value={expiresDate} onChange={(e) => setExpiresDate(e.target.value)} /></div>
          </div>
          <div><Label>Note</Label><Input value={notes} onChange={(e) => setNotes(e.target.value)} /></div>
          {modifica ? (
            <p className="text-xs text-slate-500">
              Il file allegato resta <span className="font-medium">{initial.filename}</span>.
              Per sostituirlo, carica un nuovo documento e archivia questo.
            </p>
          ) : (
            <div><Label>File *</Label>
              <Input type="file" accept=".pdf,image/*" onChange={(e) => setFile(e.target.files[0])} /></div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annulla</Button>
          <Button onClick={submit} disabled={busy} className="bg-orange-500 hover:bg-orange-600 text-white">
            {busy ? "Salvataggio..." : modifica ? "Salva modifiche" : "Carica"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
